import React, {useMemo, useRef} from 'react';

import {Pagination} from '../../Pagination';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {CardCollectionType} from '../common/types';
import {
  ArtifactCollectionCard,
  RegistrySearchCardCollection,
} from '../Registry/ArtifactCollectionCard';
import {SDKVersionProvider} from '../Registry/SDKVersionContext';
import {useSearchablePaginatedRegistryArtifactCollections} from './hooks';

const PAGE_SIZE = 15;

type RegistryCollectionsProps = {
  orgName: string;
};

export const RegistryCollections = ({orgName}: RegistryCollectionsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    currentPage: currentPaginatedCollections,
    loading: collectionsLoading,
    refetch: refetchCollections,
    hasNextPage,
    totalCollectionsLoaded,
    page,
    onPageChange,
    error,
  } = useSearchablePaginatedRegistryArtifactCollections({
    orgName,
    pageSize: PAGE_SIZE,
  });

  const collections: RegistrySearchCardCollection[] = useMemo(() => {
    return currentPaginatedCollections.map(collection => {
      return {
        collection,
        type: CardCollectionType.REGISTRY_SEARCH,
      };
    });
  }, [currentPaginatedCollections]);

  return (
    <SDKVersionProvider>
      <div
        ref={scrollRef}
        className="mb-60 w-full border-t border-moon-200 pt-32">
        <div className="flex flex-row items-center justify-between">
          <div className="mb-16 text-base font-semibold text-moon-550">
            All collections
          </div>
          {!collectionsLoading && !error && (
            <Pagination
              itemCount={totalCollectionsLoaded}
              page={page}
              perPage={PAGE_SIZE}
              onPageChange={onPageChange}
              allItemsLoaded={!hasNextPage}
            />
          )}
        </div>
        {collectionsLoading ? (
          <Loader name="global-registry-landing-page-collections" size="huge" />
        ) : error ? (
          <div className="text-base text-moon-550">
            Error loading collections
          </div>
        ) : totalCollectionsLoaded ? (
          <div>
            {collections.map(collectionInfo => {
              return (
                <ArtifactCollectionCard
                  key={collectionInfo.collection.collectionId}
                  registryName={collectionInfo.collection.registryName}
                  collectionInfo={collectionInfo}
                  refetchCollections={refetchCollections}
                  protectedAliases={
                    collectionInfo.collection.protectedArtifactAliases
                  }
                />
              );
            })}
          </div>
        ) : (
          <div className="text-base text-moon-550">No collections found</div>
        )}
      </div>
    </SDKVersionProvider>
  );
};
